<script setup lang="ts">
/**
 * Описание задачи — макет 13:459: абзац, чек-лист и блок кода.
 *
 * Просмотр и правка — один и тот же `AppRichTextEditor`, только в разных
 * режимах: markdown, который он рисует в `readonly`, обязан выглядеть ровно
 * так же, как в правке, а два разных рендерера расходятся на первом же
 * вложенном списке.
 *
 * Правка открывается щелчком по тексту и закрывается кнопками. Здесь, в
 * отличие от заголовка, сохранять по уходу фокуса нельзя: описание пишут
 * долго, отвлекаются на соседнюю вкладку и возвращаются — случайный клик мимо
 * не должен решать за человека, что он закончил.
 */
import { AppButton } from '@vinprop/design-system'
import { AppRichTextEditor } from '@vinprop/design-system/editor'
import { computed, nextTick, ref } from 'vue'
import { useI18n } from 'vue-i18n'

import { collectMentionIds, uploadEditorImage, useTrackerEditorExtensions } from '@/features/editor'

import { useIssueStore } from '../store'

const emit = defineEmits<{ failed: [error: unknown] }>()

const { t } = useI18n()
const store = useIssueStore()

const extensions = useTrackerEditorExtensions(() => store.issue?.projectId ?? '')

const isEditing = ref(false)
const isSaving = ref(false)
const draft = ref('')

const markdown = computed(() => store.issue?.descriptionMd ?? '')

const edit = async () => {
  draft.value = markdown.value
  isEditing.value = true

  await nextTick()
}

const cancel = () => {
  isEditing.value = false
}

const save = async () => {
  isSaving.value = true

  try {
    await store.setDescription(draft.value, collectMentionIds(draft.value))
    isEditing.value = false
  } catch (failure) {
    emit('failed', failure)
  } finally {
    isSaving.value = false
  }
}
</script>

<template>
  <section class="idesc">
    <template v-if="isEditing">
      <!-- В правке у текста свой потолок: иначе кнопки сохранения уезжают вниз
           ровно тогда, когда описание длинное, — а ищут их именно тогда. -->
      <AppRichTextEditor
        v-model="draft"
        preset="issue"
        autofocus
        max-height="clamp(220px, 55vh, 640px)"
        :extensions="extensions"
        :placeholder="t('issues.create.descriptionPlaceholder')"
        :upload-image="uploadEditorImage"
        @error="emit('failed', $event)"
      />

      <div class="idesc__actions">
        <AppButton variant="primary" size="small" :loading="isSaving" @click="save">
          {{ t('settings.save') }}
        </AppButton>
        <AppButton variant="secondary" size="small" @click="cancel">
          {{ t('settings.cancel') }}
        </AppButton>
      </div>
    </template>

    <!-- Пустое описание — не пустое место: без приглашения по нему не догадаются
         щёлкнуть, а вписать условия задачи чаще всего нужно именно здесь. -->
    <button v-else-if="!markdown" type="button" class="idesc__empty" @click="edit">
      {{ t('issue.description.empty') }}
    </button>

    <div v-else class="idesc__view" :title="t('issue.description.edit')" @click="edit">
      <AppRichTextEditor :model-value="markdown" preset="issue" readonly :extensions="extensions" />
    </div>
  </section>
</template>

<style scoped>
.idesc {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Просмотр не выглядит полем, но щёлкается: рамка появляется под курсором —
   иначе о правке на месте узнают только случайно. */
.idesc__view {
  margin: -8px -12px;
  padding: 8px 12px;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  cursor: text;
}

.idesc__view:hover {
  border-color: var(--color-border);
}

.idesc__empty {
  padding: 12px;
  border: 1px dashed var(--color-border);
  border-radius: var(--radius-md);
  background: none;
  color: var(--color-text-muted);
  font-family: var(--font-family-sans);
  font-size: var(--font-size-body-sm);
  text-align: left;
  cursor: text;
}

.idesc__empty:hover {
  border-color: var(--color-border-strong);
  color: var(--color-text-secondary);
}

.idesc__actions {
  display: flex;
  gap: 8px;
}
</style>
